import React, { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useApolloClient } from '@apollo/react-hooks'
import { useHistory } from 'react-router-dom'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import UncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import { AppDocsDocument } from '@paintscout/api'
import { List, ListItem, ListItemIcon, ListItemText, makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions, useUserPermissions } from '@paintscout/ui-business'
import { getFeatures, getObjectLabels } from '@paintscout/util/builder'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { closeResourceCenter } from 'shared/components/common/ResourceCenter/resourceCenterStore'
import { track } from 'shared/util/analytics'
import type { ChecklistItem } from './checklistItems'
import { claimOptionsRefresh, getChecklistItems, isChecklistComplete, releaseOptionsRefresh } from './checklistItems'

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    padding: theme.spacing(1.5, 2, 0.5),
    fontWeight: 600
  },
  progress: {
    padding: theme.spacing(0, 2),
    color: theme.palette.text.secondary
  },
  icon: {
    minWidth: 36
  },
  done: {
    color: theme.palette.success.main
  },
  doneText: {
    textDecoration: 'line-through',
    color: theme.palette.text.secondary
  }
}))

/** The checklist items for the signed-in user, gated as the New button gates creating each one. */
export function useOnboardingChecklist(): ChecklistItem[] {
  const { options } = useClientOptions()
  const { crm, crmVisible } = useFlags()
  const { permissionToCreateQuote, permissionToCreateDeal, permissionToCreateEvent } = useUserPermissions()
  const crmOn = !!crm && !!crmVisible
  const quotesOn = getFeatures({ options }).quotes.enabled && !options?.options?.quotes?.forceIntegrationQuotes

  return getChecklistItems({
    tasks: options?.options?.onboardingTasks,
    quoteLabel: getObjectLabels({ options }).quote.value,
    access: {
      quote: !!quotesOn && permissionToCreateQuote,
      deal: crmOn && permissionToCreateDeal,
      event: crmOn && permissionToCreateEvent,
      productionEvent: crmOn && permissionToCreateEvent
    }
  })
}

export interface OnboardingChecklistProps {
  /** `useOnboardingChecklist()`. The caller leaves the card out once `isChecklistComplete`. */
  items: ChecklistItem[]
}

/**
 * The onboarding checklist card (#25354), the first slot of the resource center panel. Mounted only
 * while the panel is open.
 */
export default function OnboardingChecklist({ items }: OnboardingChecklistProps) {
  const classes = useStyles()
  const history = useHistory()
  const apolloClient = useApolloClient()
  const { clientId } = useClientOptions()
  const complete = isChecklistComplete(items)

  // The API sets the flags server-side and the app never re-fetches options, so pick up a quote made
  // since load. The provider takes the new cache value unless settings have unsaved edits.
  useEffect(() => {
    if (complete || !claimOptionsRefresh(clientId, Date.now())) return
    apolloClient.query({ query: AppDocsDocument, fetchPolicy: 'network-only' }).catch((error) => {
      releaseOptionsRefresh(clientId)
      Sentry.captureException(error, { tags: { feature: 'onboardingChecklist' } })
    })
    // On open only: a refresh that ticks an item must not trigger another.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (complete) return null

  const doneCount = items.filter((item) => item.done).length

  const onSelect = (item: ChecklistItem) => {
    closeResourceCenter()
    track('checklist_item_clicked', { item: item.id })
    history.push(item.path)
  }

  return (
    <div data-testid="onboarding-checklist">
      <Typography variant="subtitle1" className={classes.heading}>
        Get started
      </Typography>
      <Typography variant="caption" component="p" className={classes.progress}>
        {doneCount} of {items.length} done
      </Typography>
      <List dense>
        {items.map((item) => (
          <ListItem
            key={item.id}
            button
            onClick={() => onSelect(item)}
            aria-label={`${item.label}, ${item.done ? 'done' : 'not done'}`}
            data-testid={`onboarding-checklist-item-${item.id}`}
          >
            <ListItemIcon className={classes.icon}>
              {item.done ? (
                <CheckCircleIcon fontSize="small" className={classes.done} titleAccess="Done" />
              ) : (
                <UncheckedIcon fontSize="small" titleAccess="Not done" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ className: item.done ? classes.doneText : undefined }}
            />
          </ListItem>
        ))}
      </List>
    </div>
  )
}
